import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, ChevronDown, ChevronUp, Flag, Star } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { BlackParallelCard } from "@/components/black1of1/BlackParallelCard";
import { CountryFlag } from "@/components/black1of1/CountryFlag";
import { countryOf } from "@/lib/countryFlags";

const PAGE_SIZE = 25;

export default function BlackOneOfOne() {
  // Split loading: lightweight summary loads first, full stats (country + latest) after.
  const [summary, setSummary] = useState(null);
  const [full, setFull] = useState(null);
  const [topStars, setTopStars] = useState(null);

  // Server-driven grid state
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [country, setCountry] = useState("");
  const [items, setItems] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [gridLoading, setGridLoading] = useState(true);

  const [expandCountries, setExpandCountries] = useState(false);

  // Fetch lightweight summary first — this is what unblocks the hero.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await api.get("/black-1of1/stats/summary").then(r => r.data).catch(() => null);
      if (!cancelled) setSummary(s);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch full stats (country grid + latest finds) in parallel — non-blocking.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const f = await api.get("/black-1of1/stats").then(r => r.data).catch(() => null);
      if (!cancelled) setFull(f);
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch Top Stars — also non-blocking, small payload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const t = await api.get("/black-1of1/top-stars").then(r => r.data).catch(() => null);
      if (!cancelled) setTopStars(t?.items || []);
    })();
    return () => { cancelled = true; };
  }, []);

  // Debounced server-side search + filter + pagination.
  const debounceRef = useRef(null);
  const controllerRef = useRef(null);
  const fetchPage = React.useCallback(async (nextPage, replace) => {
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setGridLoading(true);
    const params = { page: nextPage, page_size: PAGE_SIZE };
    if (status !== "all") params.status = status;
    if (country) params.country = country;
    if (q.trim()) params.q = q.trim();
    try {
      const r = await api.get("/black-1of1/list", { params, signal: controller.signal }).then(r => r.data);
      setItems(prev => (replace ? r.items || [] : [...prev, ...(r.items || [])]));
      setTotalMatches(r.total_matches ?? 0);
      setHasMore(!!r.has_more);
      setPage(r.page ?? nextPage);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;
      setItems([]); setTotalMatches(0); setHasMore(false);
    } finally {
      if (controllerRef.current === controller) setGridLoading(false);
    }
  }, [q, status, country]);

  // Reset to page 1 whenever query/filter changes, with 300ms debounce on q.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { fetchPage(1, true); }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, status, country]);

  const pct = summary?.percent ?? 0;
  const found = summary?.found ?? null;
  const total = summary?.total ?? null;
  const notSurfaced = summary?.not_surfaced ?? null;
  const summaryReady = summary != null;

  const countryList = full?.country_progress ?? [];
  const visibleCountries = useMemo(
    () => (expandCountries ? countryList : countryList.slice(0, 12)),
    [expandCountries, countryList],
  );

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white" data-testid="black-1of1-hub">
      <SEO
        title="Panini World Cup 2026 Black Border 1 of 1 Tracker"
        description="Track Panini FIFA World Cup 2026 Black Border 1 of 1 stickers. See which 1/1s have surfaced, which have not yet surfaced, and the latest confirmed finds."
        path="/black-1of1"
        image="/textures/not-surfaced-card.webp"
      />

      {/* Silver-foil ambient wash behind the whole tracker — restrained */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.06]" aria-hidden style={{
        backgroundImage: 'url("/textures/silver-foil.svg")',
        backgroundSize: "800px 800px",
        backgroundRepeat: "repeat",
      }} />

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#161620] via-[#0b0b0d] to-[#0b0b0d]" />
        <div className="absolute -top-24 -right-32 w-[520px] h-[520px] bg-[#d4af37]/[0.09] rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] bg-[#16a34a]/[0.09] rounded-full blur-[100px] pointer-events-none" />

        <div className="relative container-gm py-10 md:py-16">
          <div className="text-xs uppercase tracking-[0.28em] text-[#d4af37] mb-2 font-mono font-semibold">
            Panini FIFA World Cup 2026
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-3">
            Black Border <span className="text-[#d4af37]">1 of 1</span> Tracker
          </h1>
          <p className="text-white/80 max-w-xl text-base md:text-lg mb-8 md:mb-10 leading-relaxed">
            Tracking every publicly surfaced Black Border 1 of 1 sticker from the Panini FIFA World Cup 2026 collection.
          </p>

          <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,320px)] gap-6 md:gap-10 items-end mb-6">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.24em] text-white/60 mb-3 font-semibold">
                Publicly surfaced
              </div>
              <div className="font-display leading-none flex items-baseline gap-2 md:gap-4 min-h-[5rem]" data-testid="black-1of1-headline">
                {summaryReady ? (
                  <>
                    <span className="text-[#22c55e] text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] font-bold" data-testid="stat-found">{found}</span>
                    <span className="text-white/25 text-4xl sm:text-5xl md:text-6xl">/</span>
                    <span className="text-white text-4xl sm:text-5xl md:text-6xl" data-testid="stat-total">{total}</span>
                    <span className="text-[#d4af37] font-mono text-sm md:text-base ml-1 md:ml-2 tracking-widest uppercase font-bold">Found</span>
                  </>
                ) : (
                  <>
                    <span className="inline-block h-20 w-32 rounded-lg bg-white/10 animate-pulse" />
                    <span className="text-white/25 text-4xl md:text-6xl">/</span>
                    <span className="inline-block h-16 w-24 rounded-lg bg-white/10 animate-pulse" />
                  </>
                )}
              </div>

              <div className="mt-5 md:mt-6 h-4 md:h-5 rounded-full bg-white/[0.06] border border-white/10 overflow-hidden relative" data-testid="black-1of1-progress">
                {summaryReady ? (
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#16a34a] via-[#22c55e] to-[#d4af37] rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${Math.max(0.6, Math.min(100, pct))}%` }}
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent animate-pulse" />
                )}
                <div className="absolute inset-0 flex pointer-events-none">
                  {[25, 50, 75].map(t => (
                    <div key={t} style={{ left: `${t}%` }} className="absolute top-0 bottom-0 w-px bg-white/10" />
                  ))}
                </div>
              </div>
              <div className="flex justify-between mt-2 text-sm md:text-base font-mono uppercase tracking-widest">
                <span className="text-[#4ade80] font-black" data-testid="stat-percent">
                  {summaryReady ? `${pct}% surfaced` : <span className="inline-block h-5 w-24 rounded bg-white/10 animate-pulse" />}
                </span>
                <span className="text-white font-black" data-testid="stat-notsurfaced">
                  {summaryReady ? (
                    <><span className="text-white">{notSurfaced}</span> <span className="text-white/70 font-bold">not yet publicly surfaced</span></>
                  ) : (
                    <span className="inline-block h-5 w-40 rounded bg-white/10 animate-pulse" />
                  )}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <SecondaryStat label="Found" value={summaryReady ? found : null} accent="green" />
              <SecondaryStat label="Total" value={summaryReady ? total : null} />
              <SecondaryStat label="Surfaced" value={summaryReady ? `${pct}%` : null} accent="gold" />
            </div>
          </div>

          <div className="mt-4 md:mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/black-1of1/report"
              data-testid="black-1of1-report-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#d4af37] hover:bg-[#f0c458] text-black text-sm md:text-base font-bold shadow-lg shadow-[#d4af37]/10 transition"
            >
              <Flag size={16} /> Report a Black Border 1 of 1
            </Link>
            <span className="text-sm text-white/75 max-w-md leading-relaxed">
              Seen one in the wild? Share the source — we verify every sighting before the public tracker changes.
            </span>
          </div>
        </div>
      </section>

      {/* ─── TOP STARS ────────────────────────────────────────── */}
      {topStars && topStars.length > 0 && (
        <section className="border-b border-white/10">
          <div className="container-gm py-8">
            <div className="flex items-center gap-2 mb-4">
              <Star size={16} className="text-[#d4af37]" />
              <h2 className="font-display text-xl md:text-2xl uppercase tracking-widest font-bold">Top Stars</h2>
              <span className="text-xs font-mono text-white/60 uppercase tracking-widest ml-1">
                Have the biggest names been found?
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="black-1of1-top-stars">
              {topStars.map((p) => (
                <BlackParallelCard key={`top-${p.sticker_number}`} p={p} size="grid" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── LATEST FINDS ─────────────────────────────────────── */}
      {full?.latest_finds?.length > 0 && (
        <section className="border-b border-white/10">
          <div className="container-gm py-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-[#d4af37]" />
              <h2 className="font-display text-xl md:text-2xl uppercase tracking-widest font-bold">Latest Finds</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {full.latest_finds.map((p) => (
                <BlackParallelCard key={p.official_sticker_id + p.sticker_number} p={p} size="grid" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── SEARCH + FILTERS ─────────────────────────────────── */}
      <section className="border-b border-white/10 sticky top-[64px] bg-[#0b0b0d]/95 backdrop-blur-md z-20">
        <div className="container-gm py-3 md:py-4 flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search player, sticker code or country…"
              data-testid="black-1of1-search"
              className="w-full pl-9 pr-3 py-2.5 rounded-full border border-white/15 bg-white/5 focus:bg-white/10 focus:outline-none text-sm md:text-base text-white placeholder-white/50"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 md:overflow-visible">
            {[["all","All"],["found","Found"],["not_surfaced","Not surfaced"]].map(([k,l])=>(
              <button
                key={k}
                onClick={()=>setStatus(k)}
                data-testid={`black-1of1-filter-${k}`}
                className={`shrink-0 text-xs md:text-sm px-3.5 py-2 rounded-full border font-mono uppercase tracking-wider font-bold transition ${
                  status===k
                    ? (k === "found" ? "bg-[#22c55e] text-black border-[#22c55e]" : "bg-[#d4af37] text-black border-[#d4af37]")
                    : "border-white/25 text-white/80 hover:bg-white/10"
                }`}
              >
                {l}
              </button>
            ))}
            {country && (
              <button
                onClick={()=>setCountry("")}
                className="shrink-0 text-xs px-3 py-2 rounded-full border border-[#22c55e]/60 text-[#4ade80] font-mono uppercase tracking-widest font-bold inline-flex items-center gap-1.5"
              >
                <CountryFlag cc={country} className="w-4 h-3 rounded-sm" />{country} ×
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ─── COUNTRY PROGRESS ─────────────────────────────────── */}
      {countryList.length > 0 && (
        <section className="border-b border-white/10">
          <div className="container-gm py-8">
            <div className="flex items-end justify-between mb-4">
              <div>
                <h2 className="font-display text-xl md:text-2xl uppercase tracking-widest font-bold">Progress by Country</h2>
                <p className="text-xs text-white/60 mt-1 font-mono tracking-wider font-semibold">
                  {expandCountries
                    ? `Showing all ${countryList.length}`
                    : `Top ${visibleCountries.length} · ${countryList.length - visibleCountries.length} more`}
                </p>
              </div>
              <button
                onClick={()=>setExpandCountries(v => !v)}
                data-testid="black-1of1-country-expand"
                className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest font-bold text-white/85 hover:text-[#d4af37] px-3 py-2 border border-white/20 rounded-full hover:border-[#d4af37]/60 transition"
              >
                {expandCountries ? (<>Collapse <ChevronUp size={13} /></>) : (<>View all {countryList.length} countries <ChevronDown size={13} /></>)}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3" data-testid="black-1of1-countries">
              {visibleCountries.map(c => (
                <CountryTile key={c.country_code} c={c} onClick={()=>setCountry(c.country_code)} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── GRID ─────────────────────────────────────────────── */}
      <section>
        <div className="container-gm py-8">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-white/70 font-mono font-bold" data-testid="black-1of1-grid-count">
              {gridLoading && items.length === 0
                ? "Loading…"
                : <>Showing <span className="text-white">{items.length}</span> of <span className="text-white">{totalMatches}</span> {q || status !== "all" || country ? "matching " : ""}stickers</>}
              {country && <> · <span className="text-[#d4af37]">{countryOf(country)}</span></>}
              {status !== "all" && <> · <span className="text-[#d4af37]">{status === "found" ? "Found" : "Not surfaced"}</span></>}
            </div>
          </div>

          {gridLoading && items.length === 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="aspect-[3/4] rounded-xl bg-white/[0.04] border border-white/10 animate-pulse" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-white/60 border border-dashed border-white/15 rounded-xl">
              No stickers match your search.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4" data-testid="black-1of1-grid">
                {items.map((p) => (
                  <BlackParallelCard key={p.official_sticker_id + p.sticker_number} p={p} size="grid" />
                ))}
              </div>
              {hasMore && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={() => fetchPage(page + 1, false)}
                    disabled={gridLoading}
                    data-testid="black-1of1-load-more"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-[#d4af37]/40 hover:border-[#d4af37] text-[#d4af37] text-sm font-bold uppercase tracking-widest transition disabled:opacity-50"
                  >
                    {gridLoading ? "Loading…" : `Load 25 more · ${totalMatches - items.length} remaining`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ─── EDUCATIONAL CONTENT ───────────────────────────── */}
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0b0b0d] to-[#101014]">
        <div className="container-gm py-12 md:py-16 max-w-4xl">
          <div className="prose-invert space-y-10">
            <div>
              <h2 className="font-display text-2xl md:text-3xl mb-3 text-white">
                What are Panini World Cup 2026 Black Border 1 of 1 stickers?
              </h2>
              <p className="text-white/80 leading-relaxed text-base md:text-lg">
                Black Border 1 of 1 stickers are the ultimate parallel chase in the North American Panini FIFA World Cup 2026 sticker collection. Unlike a regular sticker or a parallel produced in multiple copies, a Black Border is a true one-of-one: only one Black Border copy of that particular sticker exists.
              </p>
              <p className="text-white/80 leading-relaxed text-base md:text-lg mt-3">
                That means when a specific Black Border 1 of 1 surfaces, that exact sticker has been found. There is not a second identical Black Border copy waiting to be discovered.
              </p>
              <p className="text-white/80 leading-relaxed text-base md:text-lg mt-3">
                CollectorUp created this tracker to document these discoveries as they happen and give collectors one place to see which Black Border 1 of 1 stickers have publicly surfaced and which have not yet been reported.
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl mb-3 text-white">
                How can you find a Black Border 1 of 1?
              </h2>
              <p className="text-white/80 leading-relaxed text-base md:text-lg">
                Black Border parallels are randomly inserted into eligible Panini FIFA World Cup 2026 North American sticker products. A standard Panini FIFA World Cup 2026 packet contains seven stickers, so every unopened pack is another chance for a major discovery.
              </p>
              <p className="text-white/80 leading-relaxed text-base md:text-lg mt-3">
                There is no checklist telling collectors where a particular Black Border is located. A sticker may still be sitting inside an unopened pack, already belong to a collector who has never posted it publicly, or surface later through social media, marketplaces, trading events or direct reports to CollectorUp.
              </p>
            </div>
            <div>
              <h2 className="font-display text-2xl md:text-3xl mb-3 text-white">
                What happens when one surfaces?
              </h2>
              <p className="text-white/80 leading-relaxed text-base md:text-lg">
                When credible evidence of a Black Border 1 of 1 is found, CollectorUp researches the discovery and updates the tracker. Confirmed finds can include the sticker code, player or subject, discovery evidence and other publicly available information.
              </p>
              <p className="text-white/80 leading-relaxed text-base md:text-lg mt-3">
                The goal is simple: document the hunt and eventually account for every Black Border 1 of 1 in the collection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── REPORT A FIND CTA ─────────────────────────────── */}
      <section className="border-b border-white/10">
        <div className="container-gm py-12 md:py-16">
          <div className="rounded-2xl border-2 border-[#d4af37]/40 bg-gradient-to-br from-[#141419] via-[#0b0b0d] to-[#141419] p-6 md:p-10 max-w-4xl mx-auto relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-[320px] h-[320px] bg-[#d4af37]/[0.08] rounded-full blur-[100px] pointer-events-none" />
            <div className="relative">
              <div className="text-xs font-mono uppercase tracking-[0.28em] text-[#d4af37] mb-2 font-semibold">
                Did you find one?
              </div>
              <h2 className="font-display text-2xl md:text-4xl mb-3 text-white">
                Pulled a Black Border 1 of 1?
              </h2>
              <p className="text-white/80 leading-relaxed text-base md:text-lg mb-5 max-w-2xl">
                Help us keep the tracker accurate. If you pulled one, own one, or found credible evidence of a Black Border that is not yet listed as surfaced, send it to CollectorUp for verification.
              </p>
              <Link
                to="/black-1of1/report"
                data-testid="black-1of1-report-cta-bottom"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d4af37] hover:bg-[#f0c458] text-black text-sm md:text-base font-bold shadow-lg shadow-[#d4af37]/10 transition uppercase tracking-widest"
              >
                <Flag size={16} /> Report a Black Border 1 of 1
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ───────────────────────────────────────────── */}
      <FaqSection foundCount={found} />
    </div>
  );
}

const FAQ_ITEMS = (foundCount) => [
  { q: "What is a Panini Black Border 1 of 1 sticker?",
    a: "A Black Border 1 of 1 is a unique parallel from the Panini FIFA World Cup 2026 sticker collection. Only one Black Border copy of a particular eligible sticker exists, making it a true one-of-one collectible." },
  { q: "How rare are Panini World Cup 2026 Black Border stickers?",
    a: "They are the one-of-one tier of the parallel program. Rather than having a normal print run, each Black Border sticker is unique, with only one copy of that specific Black Border produced." },
  { q: "Why are there 912 Black Border 1 of 1 stickers?",
    a: "The complete Panini FIFA World Cup 2026 collection contains 980 stickers, but Black Border 1 of 1 parallels are not produced for the 48 Team Badge stickers or the 20 FWC stickers. That leaves 912 stickers eligible for a Black Border 1 of 1." },
  { q: "Where can you find Panini World Cup 2026 Black Border 1 of 1 stickers?",
    a: "They can be randomly found in eligible North American Panini FIFA World Cup 2026 sticker products. Many may remain inside unopened packs, while others may already have been pulled without being publicly reported." },
  { q: "How many Black Border 1 of 1 stickers have been found?",
    a: foundCount != null
        ? `Our tracker currently lists ${foundCount} Black Border 1 of 1 stickers as publicly surfaced.`
        : "Our tracker maintains a live count of every Black Border 1 of 1 sticker that has publicly surfaced." },
  { q: "Which Black Border stickers have not been found?",
    a: "The CollectorUp tracker shows every sticker currently recorded as surfaced and those for which we have not yet found credible public evidence. Use the filters and search above to check a specific team, player or sticker number." },
  { q: "What should I do if I pull a Black Border 1 of 1?",
    a: "Protect the sticker immediately, avoid placing it permanently into an album, take clear photos of the front and back, and document where it was pulled. You can also report the discovery to CollectorUp so it can be researched and added to the tracker." },
  { q: "Is CollectorUp affiliated with Panini or FIFA?",
    a: "No. The CollectorUp Black Border 1 of 1 Tracker is an independently operated collector resource and is not affiliated with, endorsed by, or approved by Panini or FIFA." },
];

function FaqSection({ foundCount }) {
  const items = FAQ_ITEMS(foundCount);
  React.useEffect(() => {
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    };
    let script = document.getElementById("faq-jsonld-black-1of1");
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "faq-jsonld-black-1of1";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
    return () => {
      const s = document.getElementById("faq-jsonld-black-1of1");
      if (s) s.remove();
    };
  }, [foundCount]);
  return (
    <section className="border-b border-white/10">
      <div className="container-gm py-12 md:py-16 max-w-4xl">
        <h2 className="font-display text-2xl md:text-3xl mb-6 text-white">Frequently asked questions</h2>
        <dl className="space-y-4" data-testid="black-1of1-faq">
          {items.map(({ q, a }, i) => (
            <details key={i} className="group border border-white/10 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition">
              <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-4 md:p-5">
                <dt className="font-display text-base md:text-lg text-white pr-2">{q}</dt>
                <span className="text-[#d4af37] mt-1 shrink-0 group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <dd className="px-4 md:px-5 pb-4 md:pb-5 text-white/75 leading-relaxed text-sm md:text-base">{a}</dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  );
}

function SecondaryStat({ label, value, accent }) {
  const color = accent === "green" ? "text-[#22c55e]" : accent === "gold" ? "text-[#d4af37]" : "text-white";
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3">
      <div className="text-xs font-mono uppercase tracking-widest text-white/70 mb-1 font-bold">{label}</div>
      <div className={`font-display text-xl md:text-2xl leading-none font-bold ${color}`}>
        {value == null ? <span className="inline-block h-6 w-10 rounded bg-white/10 animate-pulse" /> : value}
      </div>
    </div>
  );
}

function CountryTile({ c, onClick }) {
  const cc = c.country_code;
  const isPct = c.percent && c.percent > 0;
  return (
    <button
      onClick={onClick}
      data-testid={`black-1of1-country-${cc}`}
      className={`text-left border rounded-lg p-3 transition group ${
        isPct
          ? "border-[#22c55e]/40 bg-[#22c55e]/[0.05] hover:border-[#22c55e]/70"
          : "border-white/10 bg-white/[0.02] hover:border-[#d4af37]/50 hover:bg-white/[0.06]"
      }`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <CountryFlag cc={cc} className="w-5 h-3.5 rounded-sm ring-1 ring-white/10" />
          <span className="font-mono text-sm tracking-widest font-bold">{cc}</span>
        </div>
        <span className={`text-xs font-mono font-bold ${isPct ? "text-[#4ade80]" : "text-white/60"}`}>{c.found}/{c.total}</span>
      </div>
      <div className="h-1 bg-white/10 rounded overflow-hidden">
        <div className={`h-full ${isPct ? "bg-[#22c55e]" : "bg-white/20"}`} style={{ width: `${Math.max(2, c.percent)}%` }} />
      </div>
    </button>
  );
}
